import { useTranslation } from 'react-i18next';
import React, { useCallback, useMemo } from 'react';
import styles from './index.module.scss';
import { IconRcPrimePicked, IconRcPrimeVote, IconRcPrimeVoted } from '@/assets/stream';
import { FeedArticleDetail } from '../../types';
import cx from 'clsx';
import { isNumber } from 'lodash';
import { usePrimePostVote, useRemainingVote } from './hooks';
import { openPrimeVoteCountModal } from '../PrimeVoteCountTip';
import { HelperTooltip } from '@/components';
import { useCurrentAddressInfo } from '@/hooks';
import { message } from 'antd';

export const PrimePostVoteBtn = ({
  feed,
  className,
  isDetail,
}: {
  feed: FeedArticleDetail;
  className?: string;
  isDetail?: boolean; // detail页样式不一样
}) => {
  const { t } = useTranslation();

  const { article } = feed;
  // list 中 prime_post_issue 后端 没有在article中返回
  const prime_post_issue = useMemo(
    () => (isDetail ? article?.prime_post_issue : feed?.prime_post_issue),
    [article?.prime_post_issue, feed?.prime_post_issue, isDetail],
  );
  const { data } = useRemainingVote(String(prime_post_issue?.issue_id));
  const { mutateAsync, isLoading } = usePrimePostVote({
    articleId: article.id,
    hasVoted: !prime_post_issue?.has_voted,
    issueId: String(prime_post_issue?.issue_id),
  });
  const { userInfo } = useCurrentAddressInfo();

  const disabled = useMemo(() => !userInfo?.web3_id, [userInfo?.web3_id]);

  const isVoted = useMemo(() => prime_post_issue?.has_voted, [prime_post_issue?.has_voted]);

  const isFinish = useMemo(
    () => isNumber(prime_post_issue?.vote_count),
    [prime_post_issue?.vote_count],
  );

  const isPicked = useMemo(() => prime_post_issue?.is_picked, [prime_post_issue?.is_picked]);

  const tip = useMemo(() => {
    if (disabled && !isFinish) {
      return t('PrimePostVoteBtn.web3IDTip');
    }
    if (isPicked) {
      return t('PrimePostVoteBtn.primeTip');
    }
    if (isVoted && !isFinish) {
      return t('PrimePostVoteBtn.cancelTip');
    }
    return undefined;
  }, [disabled, isFinish, isPicked, isVoted, t]);

  const desc = useMemo(() => {
    if (!isFinish) {
      if (disabled) {
        return t('PrimePostVoteBtn.web3IDTip');
      }
      if (isVoted) {
        return `${t('PrimePostVoteBtn.nominatedDesc', { issueId: prime_post_issue?.issue_id })} ${t('PrimePostVoteBtn.voteSuccess')}`;
      } else {
        return `${t('PrimePostVoteBtn.nominatedDesc', { issueId: prime_post_issue?.issue_id })} ${t('PrimePostVoteBtn.voteGuide')}`;
      }
    } else {
      if (isPicked) {
        if (isVoted) {
          return `${t('PrimePostVoteBtn.primePostDesc', { issueId: prime_post_issue?.issue_id })} ${t('PrimePostVoteBtn.thanks')}`;
        } else {
          return t('PrimePostVoteBtn.primePostDesc', { issueId: prime_post_issue?.issue_id });
        }
      } else {
        return t('PrimePostVoteBtn.voteEnd', { issueId: prime_post_issue?.issue_id });
      }
    }
  }, [disabled, isFinish, isPicked, isVoted, prime_post_issue?.issue_id, t]);

  const handleClick = useCallback(
    async (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      if (!data || isLoading || disabled) {
        return;
      }

      if (data.remaining_count === 0 && !prime_post_issue?.has_voted) {
        openPrimeVoteCountModal({});
        return;
      }
      if (prime_post_issue) {
        const res = await mutateAsync();
        if (res?.is_success) {
          message.success(
            prime_post_issue?.has_voted
              ? t('PrimePostVoteBtn.cancelToast')
              : t('PrimePostVoteBtn.voteToast'),
          );
        }
      }
    },
    [data, isLoading, disabled, prime_post_issue, mutateAsync, t],
  );
  return isDetail ? (
    <HelperTooltip title={!isFinish && isVoted ? t('PrimePostVoteBtn.cancelTip') : undefined}>
      <div
        className={cx(
          styles.detailBtnContainer,
          !isFinish && disabled && styles.disabled,
          isVoted && !isFinish && styles.isVoted,
          !isPicked && isFinish && styles.isFinish,
          isPicked && styles.isPicked,
        )}
        onClick={handleClick}>
        <div className={styles.voteStatus}>
          {isPicked ? <IconRcPrimePicked className={styles.pickedBtn} /> : null}
          {!isFinish ? <IconRcPrimeVote className={styles.voteBtn} /> : null}
          {isFinish ? <div className={styles.voteCount}>{prime_post_issue?.vote_count}</div> : null}
          {isVoted ? <IconRcPrimeVoted className={styles.votedBtn} /> : null}
        </div>
        <div className={styles.desc}>{desc}</div>
      </div>
    </HelperTooltip>
  ) : (
    <HelperTooltip title={tip}>
      <div
        className={cx(
          styles.container,
          !isFinish && disabled && styles.disabled,
          isVoted && !isFinish && styles.isVoted,
          !isPicked && isFinish && styles.isFinish,
          isPicked && styles.isPicked,
          className,
        )}
        onClick={handleClick}>
        {isPicked ? <IconRcPrimePicked /> : null}
        {!isFinish ? <IconRcPrimeVote className={styles.voteBtn} /> : null}
        {isFinish ? <div className={styles.voteCount}>{prime_post_issue?.vote_count}</div> : null}
        {isVoted ? <IconRcPrimeVoted /> : null}
      </div>
    </HelperTooltip>
  );
};
